// client/src/components/mischief/MarkerUnderline.js
//
// Hand-drawn underline under a word or phrase. Same visual family as
// MarkerCircle — purple ink, wobble, currentColor stroke — but flat.
// A hasty underline dragged under the letters, not a shape around them.
//
// Auto-sizes to whatever it wraps: one word gets a short line, a
// phrase gets a long one. Just wrap the emphasized text and go.
//
// Usage:
//   <p>We <MarkerUnderline>defended</MarkerUnderline> the zone.</p>
//   <MarkerUnderline color="red">missed shots</MarkerUnderline>
//   <MarkerUnderline strokeWidth={3.5}>PENALTY</MarkerUnderline>
//   <MarkerUnderline offset="close">hugs the baseline</MarkerUnderline>

import { useMemo } from 'react';

const COLOR_CLASS = {
  marker: 'text-marker',
  red:    'text-red',
  accent: 'text-accent',
};

// Offset from the baseline. Negative bottom values push the line
// down; a "close" underline hugs the letters, "far" sits with air.
// Small absolute px because underlines don't need to scale with text
// size the way circles do — a bigger word doesn't want its underline
// dragged further down; it wants a line of similar visual weight.
const OFFSET_STYLE = {
  close:  '-0.1em',
  normal: '-0.05em',
  far:    '0.1em',
};

// Three hand-authored underline paths, viewBox 100x10.
// Each drifts up and down slightly along the way — the wobble.
// Cubic Béziers again, so the curves feel drawn, not mathematical.
// A slight overshoot at either end sells it (the pen kept going a
// tick past where the word ended).
const UNDERLINE_PATHS = [
  // Variant A — dips low in the middle, small tail on the right
  'M 2 5 C 15 7, 30 3, 50 6 C 70 8, 82 4, 96 6 C 98 6.5, 99 7, 99 7',
  // Variant B — rises to a peak around the 60% mark, ends slightly low
  'M 3 6 C 18 5, 32 8, 48 5 C 62 3, 78 5, 92 7 C 95 7.5, 97 7.5, 98 7.5',
  // Variant C — steady drift up-then-down, symmetric-ish
  'M 2 7 C 20 4, 40 6, 60 4 C 78 3, 88 6, 97 5',
];

const pickPath = () =>
  UNDERLINE_PATHS[Math.floor(Math.random() * UNDERLINE_PATHS.length)];

export function MarkerUnderline({
  children,
  color = 'marker',
  strokeWidth = 2.5,
  offset = 'normal',
  className = '',
}) {
  // Stable path across re-renders — same reason as MarkerCircle.
  const path = useMemo(pickPath, []);

  const bottom = OFFSET_STYLE[offset] ?? OFFSET_STYLE.normal;
  const colorClass = COLOR_CLASS[color] ?? COLOR_CLASS.marker;

  return (
    <span className={`relative inline-block ${colorClass} ${className}`}>
      {/* The underline SVG. Positioned at the bottom of the wrapper's
          box (which matches the text's own line-box), stretched to
          the wrapper's full width. preserveAspectRatio="none" lets
          the 100x10 viewBox squash/stretch to fit any word length —
          same auto-sizing trick as MarkerCircle. */}
      <svg
        aria-hidden="true"
        className="absolute left-0 right-0 w-full pointer-events-none"
        style={{
          bottom,
          height: '0.35em',   // scales with the wrapper's font-size
        }}
        viewBox="0 0 100 10"
        preserveAspectRatio="none"
      >
        <path
          d={path}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {/* The text. No z-index needed here — the SVG sits below the
          baseline, so it never overlaps the letters anyway. */}
      <span>{children}</span>
    </span>
  );
}